import { motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { itemVariants, sectionVariants } from './constants';

/* ── Constants ────────────────────────────────────────── */

const INITIAL_VISIBLE = 12;

/* ── Props ────────────────────────────────────────────── */

interface AmenitiesSectionProps {
    amenities: string[];
}

/* ── Component ────────────────────────────────────────── */

export default function AmenitiesSection({ amenities }: AmenitiesSectionProps) {
    const shouldReduceMotion = useReducedMotion();
    const [expanded, setExpanded] = useState(false);

    /* Deduplicate and sort alphabetically */
    const sorted = [...new Set(amenities)].sort((a, b) => a.localeCompare(b));

    /* Hide section when there are no amenities */
    if (sorted.length === 0) return null;

    const hasMore = sorted.length > INITIAL_VISIBLE;
    const visible = expanded || !hasMore ? sorted : sorted.slice(0, INITIAL_VISIBLE);
    const remainingCount = sorted.length - INITIAL_VISIBLE;

    return (
        <section className="lux-space-md bg-[#fdfaf3]">
            <motion.div
                className="lux-space-px mx-auto max-w-6xl"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={shouldReduceMotion ? undefined : sectionVariants}
            >
                {/* Kicker */}
                <motion.span className="lux-caps lux-caps-lg text-[#0e2727]/60" variants={shouldReduceMotion ? undefined : itemVariants}>
                    AMENITIES
                </motion.span>

                {/* Decorative rule */}
                <hr className="mt-3 mb-8 w-20 border-t border-[#0e2727]/15" />

                {/* Grid — items use plain divs so expand/collapse works without animation conflicts */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visible.map((amenity) => (
                        <div key={amenity} className="flex items-center gap-3">
                            <Check className="h-4 w-4 shrink-0 text-[#0e2727]/40" />
                            <span className="lux-body lux-body-lg text-[#0e2727]/75">{amenity}</span>
                        </div>
                    ))}
                </div>

                {/* Toggle — ghost on light per design system */}
                {hasMore && (
                    <motion.div className="mt-8" variants={shouldReduceMotion ? undefined : itemVariants}>
                        <button
                            type="button"
                            onClick={() => setExpanded((prev) => !prev)}
                            className="lux-caps lux-caps-sm inline-flex items-center gap-2 rounded-sm border border-[#0e2727] px-6 py-2 text-[#0e2727] transition-all duration-500 hover:bg-[#0e2727] hover:text-[#E8E0D8]"
                        >
                            {expanded ? (
                                <>
                                    Show Less
                                    <ChevronUp className="h-4 w-4" />
                                </>
                            ) : (
                                <>
                                    Show {remainingCount} More Amenities
                                    <ChevronDown className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </motion.div>
                )}
            </motion.div>
        </section>
    );
}
